import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TeamView, TeamMemberView } from '@trackly/shared';
import { createDefaultTeam } from '../../../services/TeamService/team';
import { teamApi } from '../../../services/TeamService/teamApiBase';
import {
  TeamState,
  applyTeamUpdate,
  applyTeamInfoUpdate,
  applyMemberAdd,
  applyMemberUpdate,
  applyMemberRemove,
  applyCategoryUpdate,
  resetEditState,
  clearTeamState,
} from './teamTransforms';

const initialState: TeamState = {
  currentTeam: null,
  selectedTeamId: null,
  isEdited: false,
  editedFields: [],
  filters: {
    status: null,
    searchTerm: '',
  },
};

const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    updateTeam: (state, action: PayloadAction<Partial<TeamView>>) => {
      applyTeamUpdate(state, action.payload);
    },

    initializeTeam: (state) => {
      state.currentTeam = createDefaultTeam();
      resetEditState(state);
    },

    clearTeam: (state) => { clearTeamState(state); },

    setSelectedTeamId: (state, action: PayloadAction<string | null>) => {
      state.selectedTeamId = action.payload;
    },

    updateTeamInfo: (state, action: PayloadAction<{ name?: string; description?: string; status?: string }>) => {
      applyTeamInfoUpdate(state, action.payload);
    },

    addTeamMember: (state, action: PayloadAction<Omit<TeamMemberView, 'joinedDate'>>) => {
      applyMemberAdd(state, action.payload);
    },

    updateTeamMember: (state, action: PayloadAction<{ userId: string; updates: Partial<TeamMemberView> }>) => {
      applyMemberUpdate(state, action.payload.userId, action.payload.updates);
    },

    removeTeamMember: (state, action: PayloadAction<string>) => {
      applyMemberRemove(state, action.payload);
    },

    updateTeamCategories: (state, action: PayloadAction<string[]>) => {
      applyCategoryUpdate(state, action.payload);
    },

    setStatusFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.status = action.payload;
    },

    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.filters.searchTerm = action.payload;
    },

    clearFilters: (state) => {
      state.filters = { status: null, searchTerm: '' };
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        teamApi.endpoints.getTeamById.matchFulfilled,
        (state, action) => {
          state.currentTeam = action.payload;
          state.selectedTeamId = action.payload.id;
          resetEditState(state);
        },
      )
      .addMatcher(
        teamApi.endpoints.upsertTeam.matchFulfilled,
        (state, action) => {
          state.currentTeam = action.payload;
          if (action.payload.id) state.selectedTeamId = action.payload.id;
          resetEditState(state);
        },
      );
  },
});

export const {
  updateTeam,
  initializeTeam,
  clearTeam,
  setSelectedTeamId,
  updateTeamInfo,
  addTeamMember,
  updateTeamMember,
  removeTeamMember,
  updateTeamCategories,
  setStatusFilter,
  setSearchTerm,
  clearFilters,
} = teamSlice.actions;

export default teamSlice.reducer;
